import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { activitiesAPI, hotelsAPI } from '../services/api';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import { useSelectedActivities } from './SelectedActivitiesContext';
import { useDate } from './DateContext';

const SearchPage = () => {
  const navigate = useNavigate();
  const { checkIn, checkOut, setCheckIn, setCheckOut, formatDate } = useDate();
  const [activities, setActivities] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { selectedActivities, toggleActivity, setSelectedActivities, isActivitySelected } = useSelectedActivities();
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectedActivityDetail, setSelectedActivityDetail] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  
  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    destination: '',
    checkIn: checkIn ? checkIn.toISOString().split('T')[0] : '',
    checkOut: checkOut ? checkOut.toISOString().split('T')[0] : '',
    guests: 2,
    rooms: 1,
    propertyType: 'all', // all, hotel, villa, homestay
    priceRange: [0, 50000],
    rating: 0
  });

  // Sync searchFilters with DateContext
  useEffect(() => {
    if (checkIn) {
      setSearchFilters(prev => ({ ...prev, checkIn: checkIn.toISOString().split('T')[0] }));
    }
    if (checkOut) {
      setSearchFilters(prev => ({ ...prev, checkOut: checkOut.toISOString().split('T')[0] }));
    }
  }, [checkIn, checkOut]);

  // View states
  const [showResults, setShowResults] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid, list

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setError('');
      const [activitiesResponse, hotelsResponse] = await Promise.all([
        activitiesAPI.getAll(),
        hotelsAPI.getAll()
      ]);
      setActivities(activitiesResponse.data);
      setHotels(hotelsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Unable to load data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // Update DateContext with selected dates
    if (searchFilters.checkIn) {
      setCheckIn(new Date(searchFilters.checkIn));
    }
    if (searchFilters.checkOut) {
      setCheckOut(new Date(searchFilters.checkOut));
    }
    
    // If dates are selected, redirect to appropriate page based on property type
    if (searchFilters.checkIn && searchFilters.checkOut) {
      const searchParams = new URLSearchParams({
        destination: searchFilters.destination,
        checkIn: searchFilters.checkIn,
        checkOut: searchFilters.checkOut,
        guests: searchFilters.guests.toString(),
        rooms: searchFilters.rooms.toString(),
        propertyType: searchFilters.propertyType
      });
      
      // Add selected activities to URL params
      if (selectedActivities.length > 0) {
        searchParams.append('activities', selectedActivities.join(','));
      }
      
      // Redirect based on property type
      const propertyType = searchFilters.propertyType.toLowerCase();
      if (propertyType === 'hotel' || propertyType === 'all') {
        navigate(`/hotels-list?${searchParams.toString()}`);
      } else if (propertyType === 'villa' || propertyType === 'homestay') {
        navigate(`/homestay?${searchParams.toString()}`);
      } else {
        // Default to hotels page
        navigate(`/hotels-list?${searchParams.toString()}`);
      }
    } else {
      setShowResults(true);
    }
  };

  const handleActivityDetail = (activity) => {
    setSelectedActivityDetail(activity);
    setShowActivityModal(true);
  };

  const handleActivityToggle = (activityId) => toggleActivity(activityId);

  const handleHotelToggle = (hotelId) => {
    setSelectedHotels(prev => {
      if (prev.includes(hotelId)) {
        return prev.filter(id => id !== hotelId);
      } else {
        return [...prev, hotelId];
      }
    });
  };

  const handleSelectAllActivities = () => {
    if (selectedActivities.length === activities.length) {
      setSelectedActivities([]);
    } else {
      setSelectedActivities(activities.map(activity => activity._id));
    }
  };

  const handleSelectAllHotels = () => {
    if (selectedHotels.length === hotels.length) {
      setSelectedHotels([]);
    } else {
      setSelectedHotels(hotels.map(hotel => hotel._id));
    }
  };

  const filteredHotels = hotels.filter(hotel => {
    if (searchFilters.propertyType !== 'all' && hotel.category && hotel.category.toLowerCase() !== searchFilters.propertyType.toLowerCase()) {
      return false;
    }
    if (hotel.currentPrice < searchFilters.priceRange[0] || hotel.currentPrice > searchFilters.priceRange[1]) {
      return false;
    }
    if (hotel.rating < searchFilters.rating) {
      return false;
    }
    return true;
  });

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star" style={{color: '#FFD700'}}></i>);
    }

    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt" style={{color: '#FFD700'}}></i>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star" style={{color: '#FFD700'}}></i>);
    }

    return stars;
  };

  if (loading) {
    return (
      <section className="section" id="search">
        <div className="container">
          <LoadingSpinner text="Loading search options..." />
        </div>
      </section>
    );
  }

  return (
    <section className="section" id="search">
      <Helmet>
        <title>Stay, Play &amp; Explore Goa | GoaDeals</title>
        <meta name="description" content="Plan your Goa trip: choose dates, select activities and accommodation. Book hotels, homestays, villas and experiences in Goa." />
      </Helmet>
      <div className="container">
        <h2 className="section-title">Stay, Play & Explore Goa with GoaDeals</h2>
        
        {/* Search Form */}
        <div className="search-form-container">
          <div className="search-form">
            <div className="search-row">
              <div className="search-field">
                <label>Destination</label>
                <select
                  value={searchFilters.destination}
                  onChange={(e) => setSearchFilters({...searchFilters, destination: e.target.value})}
                >
                  <option value="">Select Destination</option>
                  <option value="Goa">Goa</option>
                  <option value="Andaman Nicobar">Andaman Nicobar</option>
                </select>
              </div>
              
              <div className="search-field">
                <label>Check-in</label>
                <input
                  type="date"
                  value={searchFilters.checkIn}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchFilters({...searchFilters, checkIn: value});
                    if (value) {
                      setCheckIn(new Date(value));
                    }
                  }}
                />
              </div>
              
              <div className="search-field">
                <label>Check-out</label>
                <input
                  type="date"
                  value={searchFilters.checkOut}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchFilters({...searchFilters, checkOut: value});
                    if (value) {
                      setCheckOut(new Date(value));
                    }
                  }}
                />
              </div>
              
              <div className="search-field">
                <label>Guests</label>
                <select
                  value={searchFilters.guests}
                  onChange={(e) => setSearchFilters({...searchFilters, guests: parseInt(e.target.value)})}
                >
                  <option value={1}>1 Guest</option>
                  <option value={2}>2 Guests</option>
                  <option value={3}>3 Guests</option>
                  <option value={4}>4 Guests</option>
                  <option value={5}>5+ Guests</option>
                </select>
              </div>
              
              <div className="search-field">
                <label>Rooms</label>
                <select
                  value={searchFilters.rooms}
                  onChange={(e) => setSearchFilters({...searchFilters, rooms: parseInt(e.target.value)})}
                >
                  <option value={1}>1 Room</option>
                  <option value={2}>2 Rooms</option>
                  <option value={3}>3 Rooms</option>
                  <option value={4}>4+ Rooms</option>
                </select>
              </div>
              
              <div className="search-field">
                <label>Property Type</label>
                <select
                  value={searchFilters.propertyType}
                  onChange={(e) => setSearchFilters({...searchFilters, propertyType: e.target.value})}
                >
                  <option value="all">All Properties</option>
                  <option value="hotel">Hotels</option>
                  <option value="villa">Villas</option>
                  <option value="homestay">Homestays</option>
                </select>
              </div>
              
              <button className="search-btn" onClick={handleSearch}>
                <i className="fas fa-search"></i>
                Search
              </button>
            </div>
          </div>
        </div>

        {error && (
          <ErrorMessage 
            message={error} 
            type="error" 
            onClose={() => setError('')}
          />
        )}

        {/* Activities List Section */}
        <div className="activities-list-section">
          <h3 className="section-title">Select Activities</h3>
          <div className="activities-list-container">
            {activities.map((activity) => (
              <div key={activity._id} className="activity-list-item">
                <div className="activity-checkbox-wrapper">
                  <input
                    type="checkbox"
                    id={`activity-${activity._id}`}
                    checked={isActivitySelected(activity._id)}
                    onChange={() => handleActivityToggle(activity._id)}
                  />
                  <label htmlFor={`activity-${activity._id}`} className="activity-label">
                    <span className="activity-name-list">{activity.activityName}</span>
                    <span className="activity-price-list">₹{activity.costing}</span>
                  </label>
                </div>
                <button 
                  className="activity-detail-btn"
                  onClick={() => handleActivityDetail(activity)}
                >
                  Details
                </button>
              </div>
            ))}
          </div>
          
          {selectedActivities.length > 0 && (
            <div className="selected-activities-info">
              <p>{selectedActivities.length} activity/activities selected</p>
            </div>
          )}
        </div>

        {/* Results Section */}
        {showResults && (
          <div className="search-results">
            {/* Filters */}
            <div className="results-filters">
              <div className="filter-group">
                <label>Property Type</label>
                <select
                  value={searchFilters.propertyType}
                  onChange={(e) => setSearchFilters({...searchFilters, propertyType: e.target.value})}
                >
                  <option value="all">All Properties</option>
                  <option value="hotel">Hotels</option>
                  <option value="villa">Villas</option>
                  <option value="homestay">Homestays</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Price Range</label>
                <input
                  type="range"
                  min="0"
                  max="50000"
                  step="1000"
                  value={searchFilters.priceRange[1]}
                  onChange={(e) => setSearchFilters({...searchFilters, priceRange: [0, parseInt(e.target.value)]})}
                />
                <span>₹0 - ₹{searchFilters.priceRange[1].toLocaleString()}</span>
              </div>
              
              <div className="filter-group">
                <label>Minimum Rating</label>
                <select
                  value={searchFilters.rating}
                  onChange={(e) => setSearchFilters({...searchFilters, rating: parseInt(e.target.value)})}
                >
                  <option value={0}>Any Rating</option>
                  <option value={3}>3+ Stars</option>
                  <option value={4}>4+ Stars</option>
                  <option value={4.5}>4.5+ Stars</option>
                </select>
              </div>
              
              <div className="view-controls">
                <button 
                  className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  <i className="fas fa-th"></i>
                </button>
                <button 
                  className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  <i className="fas fa-list"></i>
                </button>
              </div>
            </div>

            {/* Results Count */}
            <div className="results-header">
              <h3>Available Activities & Properties</h3>
              <div className="results-count">
                <span>{activities.length} Activities</span>
                <span>{filteredHotels.length} Properties</span>
              </div>
            </div>

            {/* Combined Results */}
            <div className="combined-results">
              {/* Activities Section */}
              <div className="activities-section">
                <div className="section-header">
                  <h4>Activities</h4>
                  <button 
                    className="select-all-btn"
                    onClick={handleSelectAllActivities}
                  >
                    {selectedActivities.length === activities.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div className={`activities-grid ${viewMode}`}>
                  {activities.map((activity) => (
                    <div key={activity._id} className={`activity-card ${viewMode}`}>
                      <div className="activity-checkbox">
                        <input
                          type="checkbox"
                          id={`activity-${activity._id}`}
                          checked={isActivitySelected(activity._id)}
                          onChange={() => handleActivityToggle(activity._id)}
                        />
                        <label htmlFor={`activity-${activity._id}`}>
                          <div className="activity-content">
                            <h5 className="activity-name">{activity.activityName}</h5>
                            <p className="activity-location">{activity.location}</p>
                            <div className="activity-plan">
                              <h6>Inclusions:</h6>
                              <p className="activity-inclusion">{activity.inclusion}</p>
                            </div>
                            <div className="activity-pricing">
                              <span className="activity-price">₹{activity.costing}</span>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hotels Section */}
              <div className="hotels-section">
                <div className="section-header">
                  <h4>Properties</h4>
                  <button 
                    className="select-all-btn"
                    onClick={handleSelectAllHotels}
                  >
                    {selectedHotels.length === filteredHotels.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div className={`hotels-grid ${viewMode}`}>
                  {filteredHotels.map((hotel) => (
                    <div key={hotel._id} className={`hotel-card ${viewMode}`}>
                      <div className="hotel-checkbox">
                        <input
                          type="checkbox"
                          id={`hotel-${hotel._id}`}
                          checked={selectedHotels.includes(hotel._id)}
                          onChange={() => handleHotelToggle(hotel._id)}
                        />
                        <label htmlFor={`hotel-${hotel._id}`}>
                          <div className="hotel-image">
                            <img src={hotel.images[0]} alt={hotel.name} />
                            <div className="hotel-rating">
                              <div className="stars">
                                {renderStars(hotel.rating)}
                              </div>
                              <span className="rating-text">{hotel.ratingText}</span>
                            </div>
                          </div>
                          <div className="hotel-content">
                            <h5 className="hotel-name">{hotel.name}</h5>
                            <p className="hotel-location">{hotel.location}</p>
                            <div className="hotel-tags">
                              {hotel.tags.slice(0, 2).map((tag, index) => (
                                <span key={index} className="tag">{tag}</span>
                              ))}
                            </div>
                            <div className="hotel-pricing">
                              <div className="price-row">
                                <span className="original-price">₹{hotel.originalPrice.toLocaleString()}</span>
                                <span className="current-price">₹{hotel.currentPrice.toLocaleString()}</span>
                              </div>
                              <div className="offer-badge">{hotel.offer}</div>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Selection Summary */}
            {(selectedActivities.length > 0 || selectedHotels.length > 0) && (
              <div className="selection-summary">
                <h3>Your Selection Summary</h3>
                <div className="summary-content">
                  {selectedActivities.length > 0 && (
                    <div className="selected-activities">
                      <h4>Selected Activities ({selectedActivities.length})</h4>
                      <div className="selected-list">
                        {selectedActivities.map(activityId => {
                          const activity = activities.find(a => String(a._id) === String(activityId));
                          return (
                            <div key={activityId} className="selected-item">
                              <span>{activity?.activityName}</span>
                              <span className="selected-price">₹{activity?.costing}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {selectedHotels.length > 0 && (
                    <div className="selected-hotels">
                      <h4>Selected Properties ({selectedHotels.length})</h4>
                      <div className="selected-list">
                        {selectedHotels.map(hotelId => {
                          const hotel = hotels.find(h => h._id === hotelId);
                          return (
                            <div key={hotelId} className="selected-item">
                              <span>{hotel?.name}</span>
                              <span className="selected-price">₹{hotel?.currentPrice}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="total-summary">
                    <div className="total-breakdown">
                      <div className="total-item">
                        <span>Activities Total:</span>
                        <span>₹{selectedActivities.reduce((total, activityId) => {
                          const activity = activities.find(a => String(a._id) === String(activityId));
                          return total + (activity?.costing || 0);
                        }, 0)}</span>
                      </div>
                      <div className="total-item">
                        <span>Properties Total:</span>
                        <span>₹{selectedHotels.reduce((total, hotelId) => {
                          const hotel = hotels.find(h => h._id === hotelId);
                          return total + (hotel?.currentPrice || 0);
                        }, 0)}</span>
                      </div>
                    </div>
                    <div className="grand-total">
                      <strong>
                        Grand Total: ₹{selectedActivities.reduce((total, activityId) => {
                          const activity = activities.find(a => String(a._id) === String(activityId));
                          return total + (activity?.costing || 0);
                        }, 0) + selectedHotels.reduce((total, hotelId) => {
                          const hotel = hotels.find(h => h._id === hotelId);
                          return total + (hotel?.currentPrice || 0);
                        }, 0)}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activity Detail Modal */}
        {showActivityModal && selectedActivityDetail && (
          <div className="modal-overlay" onClick={() => setShowActivityModal(false)}>
            <div className="activity-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{selectedActivityDetail.activityName}</h3>
                <button 
                  className="close-modal"
                  onClick={() => setShowActivityModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="activity-detail-info">
                  <div className="detail-row">
                    <strong>Location:</strong>
                    <span>{selectedActivityDetail.location}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Inclusions:</strong>
                    <span>{selectedActivityDetail.inclusion}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Cost:</strong>
                    <span className="detail-price">₹{selectedActivityDetail.costing}</span>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      if (!isActivitySelected(selectedActivityDetail._id)) {
                        handleActivityToggle(selectedActivityDetail._id);
                      }
                      setShowActivityModal(false);
                    }}
                  >
                    {isActivitySelected(selectedActivityDetail._id) ? 'Selected' : 'Select Activity'}
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowActivityModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SearchPage;

